import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import type { ArchiveListResponse, Lesson } from "@edu/shared";
import * as api from "../../lib/api.ts";
// Счётчик занятий уже написан для экрана «Уроки по заготовке». Своя копия дала
// бы «12 занятие» на одном экране и «12 занятий» на соседнем
import { meetingCountText } from "../../lib/plurals.ts";
import { agoLabel } from "../../lib/when.ts";
import { Empty } from "../../lib/Empty.tsx";
import { LoadingRows } from "../../lib/loading.tsx";
import { LessonWho } from "../lessons/LessonWho.tsx";

/**
 * Раздел «Архив»: все проведённые занятия одним списком.
 *
 * Закрывает пробел, названный в карте навигации дизайнера
 * (docs/design/cabinet-ux-v1.html): в архив урока можно было попасть только
 * через сам урок, а списка не было вовсе. Вопрос «что мы проходили с Marco за
 * месяц» преподаватель задаёт постоянно, и до этого экрана ответ на него
 * приходилось собирать по названиям в общем списке уроков.
 *
 * Пять решений:
 *
 * 1. **Фильтрует база, а не браузер.** Оба фильтра уезжают в запрос. Тянуть
 *    всю историю ради выпадающего списка нельзя: у преподавателя с историей
 *    уроков тысячи, а канал бывает 128 кбит/с (раздел 3.1 CLAUDE.md)
 * 2. **Фильтры живут в адресе, а не в `useState`.** Отфильтрованный архив
 *    пересылается ссылкой, а кнопка «назад» браузера снимает последний фильтр,
 *    а не выкидывает из раздела. Состояние, дублирующее адрес, разъезжается с ним
 * 3. **Списки фильтров приезжают с сервера вместе с уроками**, посчитанные по
 *    всему архиву. Не из `/v1/students` и `/v1/plans`: там живые ученики и
 *    заготовки, а здесь нужны те, с кем занятия РЕАЛЬНО были, включая отвязанных
 * 4. **Строки с фиксированными слотами, а не карточки.** Таблица дизайнера
 *    откладывалась ровно из-за одной её колонки, доли верных ответов, которой у
 *    нас всё ещё нет. Решение пересмотрено владельцем 18.08.2026: таблица
 *    делается сейчас и без этой колонки, потому что структуру списку даёт не
 *    она, а одинаковый порядок слотов в каждой строке. Карточка собирала
 *    подпись из того, что не `null`, и у урока без ученика заготовка вставала
 *    на место имени
 * 5. **Группировка по месяцам.** Архив читают по времени («что было в мае»), а
 *    не по алфавиту, поэтому дата стоит первой колонкой, а месяц отбивает
 *    группы. Порядок строк при этом не наш, а серверный: список приезжает уже
 *    отсортированным, и пересортировка на клиенте разошлась бы с отсечкой
 *    `ARCHIVE_LIST_LIMIT`, то есть показала бы не тот хвост
 *
 * Доли верных ответов и итога занятия здесь нет намеренно: они считаются по
 * `exercise_attempt` и это отдельная работа.
 */

/**
 * Месяц заголовка группы: «Август 2026».
 *
 * Собран из частей, а не отформатирован целиком: `ru-RU` выдаёт «август 2026 г.»,
 * и обе особенности этой строки заголовку мешают. «г.» здесь лишнее слово, а
 * строчная буква в начале строки читается как обрывок предложения.
 */
const monthFmt = new Intl.DateTimeFormat("ru-RU", { month: "long", year: "numeric" });

function monthText(ts: number): string {
  const parts = monthFmt.formatToParts(new Date(ts));
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  const year = parts.find((p) => p.type === "year")?.value ?? "";
  return `${month.slice(0, 1).toUpperCase()}${month.slice(1)} ${year}`;
}

/** Ключ группы. Год в нём обязателен: август 2025 и август 2026 разные группы. */
function monthKey(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getFullYear())}-${String(d.getMonth())}`;
}

/** Имена параметров адреса. Читают их двое, поэтому не литералы по месту. */
const P_STUDENT = "student";
const P_PLAN = "plan";

export function ArchiveListPage() {
  const [params, setParams] = useSearchParams();
  const student = params.get(P_STUDENT) ?? "";
  const plan = params.get(P_PLAN) ?? "";

  const [data, setData] = useState<ArchiveListResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  /**
   * Загрузка ПОВЕРХ уже показанного списка. Отдельный флаг, а не `data = null`:
   * иначе при каждой смене фильтра экран схлопывался бы в «Загрузка…», и
   * выпадающий список прыгал бы под курсором на слабом канале.
   */
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    void (async () => {
      try {
        const res = await api.listArchive({
          ...(student === "" ? {} : { studentUserId: student }),
          ...(plan === "" ? {} : { planId: plan }),
        });
        if (!alive) return;
        setData(res);
        setErr(null);
      } catch (e) {
        if (!alive) return;
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [student, plan]);

  /**
   * Смена фильтра заменяет запись в истории, а не добавляет её: иначе «назад»
   * после трёх переключений подряд пришлось бы нажимать три раза, чтобы уйти с
   * экрана. Сам факт фильтрации в адресе при этом остаётся, и ссылку можно
   * переслать.
   */
  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  const lessons = data?.lessons ?? [];
  const hasFilters = (data?.students.length ?? 0) > 0 || (data?.plans.length ?? 0) > 0;
  const filtered = student !== "" || plan !== "";
  // Одно «сейчас» на всю отрисовку: с `Date.now()` в каждой строке соседние
  // занятия одной минуты могут получить разные ярлыки
  const now = Date.now();

  return (
    <main className="home home--list">
      {/* Главного действия у архива нет и быть не может: он отвечает на вопрос
          о прошлом, а создать прошлое нечем. Место справа стоит пустым */}
      <header className="head">
        <div className="head__line">
          <h1 className="head__h">Архив</h1>
          {/*
            ⚠️ Числа у обрезанного списка больше НЕТ, и убрано оно 02.09.2026.
            Потолок стоит на УРОКАХ, а строк выходит меньше: занятие группы это N
            уроков, схлопнутых в одну строку. «Показаны последние 100» над восемью
            строками это прямая неправда, а честное число потребовало бы второго
            запроса ради подписи.
          */}
          {data !== null && !busy && !data.truncated && (
            <span className="head__n">{meetingCountText(lessons.length)}</span>
          )}
        </div>
        {data !== null && !busy && data.truncated && (
          <div className="head__sub">показаны только недавние занятия</div>
        )}
      </header>

      {err !== null && <p className="err">{err}</p>}

      {/*
        ⚠️ **Полоса фильтров стоит НАД карточкой, а не внутри неё** (14.09.2026,
        долг подхода 2е). До этого дня оба выпадающих списка лежали в `card__form`
        внутри той же карточки, что и таблица, то есть в одной коробке оказывались
        и вопрос к списку, и сам список. Полоса это ряд контролов НАД блоком, у
        неё нет ни рамки, ни фона (разбор у `.bar` в `styles.css`), и ровно так же
        она устроена на учениках и в домашке. Две коробки подряд читаются как два
        раздела там, где раздел один.
      */}
      {data !== null && hasFilters && (
        <div className="bar">
          {/*
            Каждый список рисуется, только когда в нём есть из чего выбирать.
            Поле с единственным пунктом «все» это поле, которое ничего не
            делает, и у ученика тут не остаётся ни одного: фильтры считает
            сервер и ему их не присылает
          */}
          {data.students.length > 0 && (
            <select
              className="fld"
              value={student}
              onChange={(e) => setFilter(P_STUDENT, e.target.value)}
              aria-label="Ученик"
            >
              <option value="">Все ученики</option>
              {data.students.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
          )}
          {data.plans.length > 0 && (
            <select
              className="fld"
              value={plan}
              onChange={(e) => setFilter(P_PLAN, e.target.value)}
              aria-label="Заготовка"
            >
              <option value="">Все заготовки</option>
              {data.plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          )}
          {/*
            Снятие фильтра это отказ от уже сделанного выбора, а не действие
            над архивом: тихий вес. Акцента на экране нет вовсе, и это тот же
            случай, что у расписания: архив показывает прошлое, а создать в
            нём нечего. Кнопки «Разбор» акцента не получают по правилу списка:
            их на экране до сотни
          */}
          {filtered && (
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => setParams(new URLSearchParams(), { replace: true })}
            >
              Сбросить
            </button>
          )}
        </div>
      )}

      <section className="card">
        {data === null ? (
          // Пусто, если и данных нет, и загрузка кончилась: значит был отказ, и
          // он уже написан красной строкой выше
          busy ? (
            <LoadingRows list="list--archive" cells={5} />
          ) : null
        ) : lessons.length === 0 ? (
          /*
            Два разных случая одной плашки, и кнопки нет ни у одного. У отбора
            выход стоит тут же в полосе фильтров («Сбросить»), и вторая такая же
            кнопка в плашке была бы указанием словами на то, что и так рядом. У
            пустого архива выхода нет вовсе: он наполняется сам, проведёнными
            занятиями, и звать тут не к чему.
          */
          filtered ? (
            <Empty icon="search" title="Ничего не нашлось" text="По этому фильтру занятий нет." />
          ) : (
            <Empty
              icon="board"
              title="Проведённых занятий пока нет"
              text="Урок попадает сюда после того, как на его доске что-нибудь сохранилось."
            />
          )
        ) : (
          <ul className="list list--archive">
            <li className="list__head" aria-hidden="true">
              <span>Дата</span>
              <span>Занятие</span>
              <span>Ученик</span>
              <span>Заготовка</span>
              <span>Действие</span>
            </li>
            <ArchiveRows lessons={lessons} now={now} />
          </ul>
        )}
      </section>
    </main>
  );
}

/**
 * Строки архива с заголовками месяцев.
 *
 * Заголовок ставится там, где месяц сменился относительно ПРЕДЫДУЩЕЙ строки, а
 * не группировкой в `Map`: группировка перебрала бы порядок, а он приходит с
 * сервера и обязан сохраниться (решение 5 в шапке файла).
 */
function ArchiveRows({ lessons, now }: { lessons: Lesson[]; now: number }) {
  const out: ReactNode[] = [];
  let month = "";

  for (const lesson of lessons) {
    const key = monthKey(lesson.createdAt);
    if (key !== month) {
      month = key;
      out.push(
        <li className="list__group" key={`m:${key}`}>
          {monthText(lesson.createdAt)}
        </li>,
      );
    }
    out.push(<ArchiveRow key={lesson.id} lesson={lesson} now={now} />);
  }

  return <>{out}</>;
}

function ArchiveRow({ lesson, now }: { lesson: Lesson; now: number }) {
  const when = agoLabel(lesson.createdAt, now);

  return (
    <li className="row">
      {/*
        Дата создания, а не «дата занятия»: по ней же идёт сортировка, и она
        опирается на индексы. Столбец `started_at` дал бы у одного урока из
        сотни другое число, а у списка при этом сломался бы порядок
      */}
      <span
        className="cell row__when"
        data-label="Дата"
        data-tone={when.tone}
        title={when.title}
      >
        {when.text}
      </span>

      <span className="cell row__name">{lesson.title}</span>

      {/*
        Колонка общая со списком уроков: у занятия группы там и там стоит
        название группы, а не имя одного из участников
      */}
      <LessonWho lesson={lesson} />

      <PlanCell lesson={lesson} />

      <span className="cell row__act">
        <Link className="btn btn--sm" to={`/archive/${lesson.id}`}>
          Разбор
        </Link>
      </span>
    </li>
  );
}

/**
 * Слот заготовки. Четыре случая, и слот занят во всех четырёх: пустой слот
 * схлопнулся бы, и колонка «Действие» у этой строки уехала бы влево, то есть
 * вернулась бы ровно та беда, ради которой список переписан строками.
 */
function PlanCell({ lesson }: { lesson: Lesson }) {
  /*
    Заготовка ссылкой на источник, ровно как просит карта навигации: «из урока
    или архива попасть в заготовку». Название приезжает только преподавателю
    этого урока, поэтому лишней проверки роли здесь нет
  */
  if (lesson.planId != null && lesson.planTitle != null) {
    return (
      <span className="cell" data-label="Заготовка">
        <Link className="textlink" to={`/plans/${lesson.planId}`}>
          {lesson.planTitle}
        </Link>
      </span>
    );
  }

  /*
    Заготовка есть, а названия нет: значит смотрит не преподаватель этого урока,
    архив открыт и ученику. Писать ему «без заготовки» было бы враньём, а
    показать имя чужого материала нам нечем: сервер его не присылает
  */
  if (lesson.planId != null) {
    return (
      <span className="cell row__void" data-label="Заготовка">
        не видна
      </span>
    );
  }

  /*
    Урок без заготовки это норма (создан руками, или заготовки не было вовсе), а
    вот стёртая заготовка при взятом материале это отдельный случай, и молчать о
    нём значит выдать его за первый
  */
  if (lesson.planMaterializedAt != null) {
    return (
      <span className="cell row__void" data-label="Заготовка">
        заготовка удалена
      </span>
    );
  }

  return (
    <span className="cell row__void" data-label="Заготовка">
      без заготовки
    </span>
  );
}
